import { Grid, Box, Stack, HStack, Text, Icon } from '@chakra-ui/react';
import { getImageUrl, isSold } from '@habity/services';
import { ImageVariant, NestedHome } from '@habity/types';
import { IoMdPhotos } from 'react-icons/io';
import { useEffect, useState } from 'react';
import { ImageGridItem } from './GridItem';
import { HomeProfileOverlayBadges } from '~/components/homes/profile/components/OverlayBadges';

const GRID_MAX_SIZE = 6;

type ImageGridProps = {
  home: NestedHome;
  handleOpenGallery?: (...args: any) => void;
  gridLayoutPreference: number;
};

export const ImageGrid = ({
  home,
  handleOpenGallery,
  gridLayoutPreference,
}: ImageGridProps) => {
  const { images } = home || {};

  const [gridImageUrls, setGridImageUrls] = useState<string[]>(
    Array(GRID_MAX_SIZE).fill(undefined)
  );

  const fetchGridImages = () => {
    for (let i = 0; i < GRID_MAX_SIZE; i++) {
      const imageVariant =
        i === 0 || i === 1 ? (`profile-${i}` as ImageVariant) : 'profile';
      if (images && typeof images[i] !== 'undefined') {
        const imageUrl = getImageUrl(images[i], imageVariant);
        // Set i-th element to image url
        setGridImageUrls((prev) =>
          prev.map((elem, idx) => (idx === i ? imageUrl : elem))
        );
      }
    }
  };

  useEffect(() => {
    fetchGridImages();
  }, [images]);

  return (
    <Stack spacing={3} position="relative">
      {!images || images.length === 0 ? (
        <GridLayoutOfOne
          gridImageUrls={['/eo/images/eo-home-missing-img.jpg']}
          onImageClick={handleOpenGallery}
        />
      ) : images.length < 3 ? (
        <GridLayoutOfOne
          gridImageUrls={gridImageUrls}
          onImageClick={handleOpenGallery}
        />
      ) : images.length < 5 ? (
        <GridLayoutOfThree
          gridImageUrls={gridImageUrls}
          onImageClick={handleOpenGallery}
        />
      ) : gridLayoutPreference === 5 ? (
        <GridLayoutOfFive
          gridImageUrls={gridImageUrls}
          onImageClick={handleOpenGallery}
        />
      ) : gridLayoutPreference === 6 ? (
        <GridLayoutOfSix
          gridImageUrls={gridImageUrls}
          onImageClick={handleOpenGallery}
        />
      ) : null}

      {/* Overlay badges */}
      <Box position="absolute" left={3} top={4}>
        <HomeProfileOverlayBadges
          home={home}
          showInspectedBadge={!isSold(home)}
          showNextViewingBadge={!isSold(home)}
          showBiddingBadge={!isSold(home)}
        />
      </Box>
    </Stack>
  );
};

interface GridLayoutProps {
  gridImageUrls: string[];
  onImageClick?: (...args: any) => void;
}

export function GridLayoutOfSix({
  gridImageUrls,
  onImageClick,
}: GridLayoutProps) {
  return (
    <Stack spacing={3}>
      {/* First row */}
      <Grid
        templateColumns="3fr 2fr"
        justifyContent="space-between"
        gap={3}
        py={0}
        my={0}
      >
        {[0, 1].map((i) => (
          <ImageGridItem
            key={`grid-image-${i}`}
            imageUrl={gridImageUrls?.[i]}
            boxProps={{ height: '420px' }}
            onClick={onImageClick}
          />
        ))}
      </Grid>

      {/* Second row */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        justifyContent="space-between"
        gap={3}
        py={0}
        my={0}
      >
        {[2, 3, 4, 5].map((i) => (
          <ImageGridItem
            key={`grid-image-${i}`}
            imageUrl={gridImageUrls?.[i]}
            boxProps={{ height: '220px' }}
            onClick={onImageClick}
          >
            {i === 5 && (
              <Box position="absolute" inset={0} zIndex={2} cursor="pointer">
                <Box
                  backgroundColor="black"
                  blendMode="multiply"
                  opacity={0.6}
                  position="absolute"
                  inset="0"
                  zIndex={3}
                />
                <HStack
                  position="relative"
                  justify="center"
                  align="center"
                  color="white"
                  w="full"
                  h="full"
                  zIndex={4}
                >
                  <Icon as={IoMdPhotos} boxSize="14px" />
                  <Text size="sm" fontWeight="semibold">
                    Alla bilder
                  </Text>
                </HStack>
              </Box>
            )}
          </ImageGridItem>
        ))}
      </Grid>
    </Stack>
  );
}

export function GridLayoutOfFive({
  gridImageUrls,
  onImageClick,
}: GridLayoutProps) {
  return (
    <HStack spacing={3}>
      {/* Left */}
      <ImageGridItem
        key={`grid-image-0`}
        imageUrl={gridImageUrls?.[0]}
        boxProps={{
          height: '380px',
        }}
        onClick={onImageClick}
      />

      {/* Right */}
      <Grid
        templateColumns="repeat(2, 350px)"
        justifyContent="space-between"
        gap={3}
        py={0}
        my={0}
      >
        {[1, 2, 3, 4].map((i) => (
          <ImageGridItem
            key={`grid-image-${i}`}
            imageUrl={gridImageUrls?.[i]}
            boxProps={{ h: '184px' }}
            onClick={onImageClick}
          >
            {i === 4 && (
              <Box position="absolute" inset={0} zIndex={2} cursor="pointer">
                <Box
                  backgroundColor="black"
                  blendMode="multiply"
                  opacity={0.6}
                  position="absolute"
                  inset="0"
                  zIndex={3}
                />
                <HStack
                  position="relative"
                  justify="center"
                  align="center"
                  color="white"
                  w="full"
                  h="full"
                  zIndex={4}
                >
                  <Icon as={IoMdPhotos} boxSize="14px" />
                  <Text size="sm" fontWeight="semibold">
                    Alla bilder
                  </Text>
                </HStack>
              </Box>
            )}
          </ImageGridItem>
        ))}
      </Grid>
    </HStack>
  );
}

export function GridLayoutOfThree({
  gridImageUrls,
  onImageClick,
}: GridLayoutProps) {
  return (
    <Stack spacing={3}>
      <Grid
        templateColumns="repeat(3, 1fr)"
        justifyContent="space-between"
        gap={3}
        py={0}
        my={0}
      >
        {[0, 1, 2].map((i) => (
          <ImageGridItem
            key={`grid-image-${i}`}
            imageUrl={gridImageUrls?.[i]}
            boxProps={{ height: '420px' }}
            onClick={onImageClick}
          />
        ))}
      </Grid>
    </Stack>
  );
}

export function GridLayoutOfOne({
  gridImageUrls,
  onImageClick,
}: GridLayoutProps) {
  return (
    <>
      <ImageGridItem
        key="single-spanning-image"
        imageUrl={gridImageUrls?.[0]}
        boxProps={{ height: '653px' }}
        onClick={onImageClick}
      />
    </>
  );
}
